import React from 'react';

import {
  useDeleteUsersMutation,
  useInvestorsTabPaneQuery,
  useUpdateCompanyProfileVisibilityMutation,
  useVerifyInvestorMutation
} from 'generated/graphql';
import _ from 'lodash';
import {
  baseTypes,
  pagination,
  useLoading
} from 'modules';
import moment from 'moment';



export interface IRow {
  id: string;
  isVisible: boolean;
  createdTimestamp: moment.Moment;
  investorId: string;
  firstName: string;
  lastName: string;
  companyProfileId: string;
  companyName: string;
  email: string;
  phoneNumber: string;
  investmentAreas: string[];
  investmentStages: string[];
  investorProfileId: string;
  isRidgeVerified: boolean;
  isClaimed: boolean;
}

export interface IData {
  page: baseTypes.IPage<IRow>;
}

export function useInvestorsTabPane(paginationInput: pagination.IListPagePaginationParams) {
  const tuple = useInvestorsTabPaneQuery({
    variables: {
      paginationInput
    }
  });
  const [verifyInvestor, verifyInvestorTuple] = __useVerifyInvestorMutation();
  const [deleteUsers, deleteUsersTuple] = __useDeleteUsersMutation();
  const [updateCompanyProfileVisibility, updateCompanyProfileVisibilityTuple] = __useUpdateCompanyProfileVisibility();
  const loading = useLoading([tuple, verifyInvestorTuple, deleteUsersTuple, updateCompanyProfileVisibilityTuple]);



  const data = React.useMemo((): IData => ({
    page: {
      nodes: _.map(tuple.data?.users.nodes, (user): IRow => ({
        id: user.id,
        isVisible: user.companyProfile?.isVisible ?? false,
        createdTimestamp: moment(user.createdTimestamp, moment.ISO_8601),
        isClaimed: user.isClaimed,
        investorId: user.investorProfile?.id ?? '',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        companyProfileId: user.companyProfile?.id ?? '',
        companyName: user.companyProfile?.name ?? '',
        phoneNumber: user.companyProfile?.phoneNumber ?? '',
        email: user.email ?? '',
        investmentStages: _.map(user.companyProfile?.investmentStages, stage => stage.displayName),
        investmentAreas: _.map(user.companyProfile?.investmentAreas, area => area.displayName),
        investorProfileId: user.investorProfile?.id ?? '',
        isRidgeVerified: user.investorProfile?.isVerifiedInvestor ?? false
      })),
      pages: tuple.data?.users.pageInfo.pages ?? [],
      totalRows: tuple.data?.users.pageInfo.totalRows ?? 0
    }
  }), [tuple]);



  return {
    ...tuple,
    loading,
    data,
    mutations: {
      verifyInvestor,
      updateCompanyProfileVisibility: async (companyProfileId: string, isVisible: boolean) => {
        await updateCompanyProfileVisibility(companyProfileId, isVisible);
        tuple.refetch();
      },
      deleteUsers: async (ids: string[]) => {
        await deleteUsers(ids);
        await tuple.refetch();
      }
    }
  };
}



function __useVerifyInvestorMutation() {
  const [fnMutate, tuple] = useVerifyInvestorMutation();

  async function fnWrappedMutate(investorId: string) {
    await fnMutate({ variables: { investorId } });
  }

  return [
    fnWrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const;
}



function __useDeleteUsersMutation() {
  const [fnMutate, tuple] = useDeleteUsersMutation();

  async function fnWrappedMutate(ids: string[]) {
    await fnMutate({ variables: { ids } });
  }

  return [
    fnWrappedMutate,
    {
      ...tuple,
      data: null
    }
  ] as const;
}



function __useUpdateCompanyProfileVisibility() {
  const [fnMutate, tuple] = useUpdateCompanyProfileVisibilityMutation({
  });

  const fnWrappedMutate = React.useCallback(
    async (companyProfileId: string, isVisible: boolean) => {
      await fnMutate({
        variables: {
          input: {
            companyProfileId,
            isVisible
          }
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
